import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Users,
  BookOpen,
  FileText,
  GraduationCap,
  Trash2,
  Upload,
  Plus,
  Save,
  Eye,
  GripVertical,
  RefreshCw,
  Settings2,
  Book,
  Type,
  Brain,
  Layers,
  ListChecks,
  CreditCard,
  Link2,
  MousePointerClick,
  WalletCards,
  UserPlus,
  Copy,
  MessageCircle,
} from "lucide-react";
import LessonEditor from "@/components/LessonEditor";
import AdminSupportMessages from "@/pages/AdminSupportMessages";
import { t } from "@/i18n";
const Stat = ({ icon: I, label, value }) => (
  <div className="rounded-2xl border bg-card p-5">
    <I size={20} className="text-primary mb-2" />
    <div className="font-head text-2xl font-bold">{value}</div>
    <div className="text-sm text-muted-foreground">{label}</div>
  </div>
);
function TemplateButton({ type, label }) {
  const dl = async () => {
    try {
      const { data } = await api.get(`/admin/templates/${type}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(
        new Blob([data], { type: "text/csv;charset=utf-8" }),
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = `template_${type}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Échec téléchargement template");
    }
  };
  return (
    <button
      onClick={dl}
      className="px-3 py-2 rounded-xl border text-xs font-medium hover:bg-secondary flex items-center gap-1"
    >
      <Save size={13} /> {label || "Template CSV"}
    </button>
  );
}
function BillingAdminPanel({ users, reloadUsers }) {
  const [email, setEmail] = useState("");
  const [accountType, setAccountType] = useState("free");
  const [plan, setPlan] = useState("monthly");
  const [duration, setDuration] = useState("");
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", name: "" });
  const [createdCredentials, setCreatedCredentials] = useState(null);
  const loadClaims = () =>
    api
      .get("/admin/billing/creator-claims")
      .then((r) => setClaims(r.data || []))
      .catch(() => {});
  useEffect(() => {
    loadClaims();
  }, []);
  const updateAccount = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await api.put("/admin/billing/users", {
        email: email.trim(),
        account_type: accountType,
        plan,
        duration_days: duration ? Number(duration) : null,
      });
      toast.success("Accès utilisateur mis à jour.");
      setEmail("");
      reloadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Utilisateur introuvable.");
    } finally {
      setLoading(false);
    }
  };
  const saveClaim = async (claim, status, views) => {
    try {
      await api.put(`/admin/billing/creator-claims/${claim.id}`, {
        status,
        verified_views: Number(views) || 0,
        note: claim.note || "",
      });
      toast.success("Demande vidéo mise à jour.");
      loadClaims();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de validation.");
    }
  };
  const createUser = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/users/create", newUser);
      setCreatedCredentials(data);
      setNewUser({ email: "", name: "" });
      reloadUsers();
      toast.success("Compte créé.");
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Impossible de créer le compte.",
      );
    }
  };
  return (
    <div className="space-y-5">
      <div className="rounded-2xl border bg-card p-5">
        <h2 className="mb-1 flex items-center gap-2 font-head text-xl font-bold">
          <UserPlus size={19} /> {t("admin.creer_un_compte_pour_quelqu_un")}
        </h2>
        <p className="mb-4 text-sm text-muted-foreground">
          {t("admin.le_compte_recoit_un_essai_de")}
        </p>
        <form
          onSubmit={createUser}
          className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]"
        >
          <input
            required
            type="email"
            placeholder={t("admin.adresse_email")}
            value={newUser.email}
            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
            className="rounded-xl border bg-background px-3 py-2"
          />
          <input
            placeholder={t("admin.nom_facultatif")}
            value={newUser.name}
            onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
            className="rounded-xl border bg-background px-3 py-2"
          />
          <button className="rounded-xl bg-primary px-4 py-2 font-semibold text-primary-foreground">
            {t("admin.creer_le_compte")}
          </button>
        </form>
        {createdCredentials && (
          <div className="mt-4 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm">
            <div className="font-semibold text-amber-800 dark:text-amber-200">
              {t("admin.a_transmettre_maintenant_ce_mot_de")}
            </div>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              <div>
                <span className="text-xs text-muted-foreground">{t("admin.email")}</span>
                <div className="font-mono">{createdCredentials.email}</div>
              </div>
              <div>
                <span className="text-xs text-muted-foreground">
                  {t("admin.mot_de_passe_temporaire")}
                </span>
                <div className="font-mono font-bold">
                  {createdCredentials.temporary_password}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() =>
                navigator.clipboard.writeText(
                  `Email : ${createdCredentials.email}\nMot de passe : ${createdCredentials.temporary_password}`,
                )
              }
              className="mt-3 flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-semibold hover:bg-background"
            >
              <Copy size={13} /> {t("admin.copier_les_identifiants")}
            </button>
          </div>
        )}
      </div>
      <div className="rounded-2xl border bg-card p-5">
        <h2 className="font-head text-xl font-bold mb-1">
          {t("admin.attribuer_un_acces_par_email")}
        </h2>
        <p className="text-sm text-muted-foreground mb-4">
          {t("admin.accorde_manuellement_un_essai_un_acces")}
        </p>
        <form
          onSubmit={updateAccount}
          className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2"
        >
          <input
            required
            type="email"
            list="admin-user-emails"
            placeholder={t("admin.email_exemple_com")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="lg:col-span-2 px-3 py-2 rounded-xl border bg-background"
          />
          <datalist id="admin-user-emails">
            {users.map((u) => (
              <option key={u.id} value={u.email} />
            ))}
          </datalist>
          <select
            value={accountType}
            onChange={(e) => setAccountType(e.target.value)}
            className="px-3 py-2 rounded-xl border bg-background"
          >
            <option value="free">{t("admin.acces_gratuit")}</option>
            <option value="paid">{t("admin.payant")}</option>
            <option value="trial">{t("admin.essai")}</option>
            <option value="expired">{t("admin.expirer")}</option>
          </select>
          <select
            value={plan}
            onChange={(e) => setPlan(e.target.value)}
            className="px-3 py-2 rounded-xl border bg-background"
          >
            <option value="monthly">{t("admin.1_mois")}</option>
            <option value="six_months">{t("admin.6_mois")}</option>
            <option value="annual">{t("admin.1_an")}</option>
            <option value="lifetime">{t("admin.a_vie")}</option>
          </select>
          <div className="flex gap-2">
            <input
              type="number"
              min="1"
              placeholder={t("admin.jours_optionnel")}
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              className="min-w-0 flex-1 px-3 py-2 rounded-xl border bg-background"
            />
            <button
              disabled={loading}
              className="rounded-xl bg-primary px-4 py-2 font-semibold text-primary-foreground disabled:opacity-50"
            >
              {t("admin.appliquer")}
            </button>
          </div>
        </form>
      </div>
      <div className="rounded-2xl border bg-card overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h2 className="font-head text-xl font-bold">
              {t("admin.demandes_d_acces_par_videos")}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t("admin.verifie_les_vues_cumulees_puis_approuve")}
            </p>
          </div>
          <button
            onClick={loadClaims}
            className="rounded-lg border p-2 hover:bg-secondary"
          >
            <RefreshCw size={15} />
          </button>
        </div>
        {claims.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground">
            {t("admin.aucune_demande")}
          </div>
        ) : (
          claims.map((claim) => (
            <div
              key={claim.id}
              className="border-b last:border-0 p-5 space-y-3"
            >
              <div className="flex flex-wrap justify-between gap-2">
                <div>
                  <div className="font-semibold">{claim.email}</div>
                  <div className="text-xs text-muted-foreground">
                    {claim.video_urls.length} {t("admin.video_s_envoyee_le")}{" "}
                    {new Date(claim.created_at).toLocaleDateString("fr-FR")}
                  </div>
                </div>
                <span
                  className={`text-xs font-semibold ${claim.status === "approved" ? "text-emerald-600" : claim.status === "rejected" ? "text-destructive" : "text-amber-600"}`}
                >
                  {claim.status}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {claim.video_urls.map((url) => (
                  <a
                    key={url}
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-primary underline break-all"
                  >
                    {url}
                  </a>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  id={`views-${claim.id}`}
                  type="number"
                  min="0"
                  defaultValue={claim.verified_views || 0}
                  placeholder={t("admin.vues_verifiees")}
                  className="w-40 px-3 py-2 rounded-xl border bg-background"
                />
                <button
                  onClick={() =>
                    saveClaim(
                      claim,
                      "approved",
                      document.getElementById(`views-${claim.id}`)?.value,
                    )
                  }
                  className="rounded-xl bg-emerald-600 px-3 py-2 text-sm font-semibold text-white"
                >
                  {t("admin.approuver_et_attribuer")}
                </button>
                <button
                  onClick={() =>
                    saveClaim(
                      claim,
                      "rejected",
                      document.getElementById(`views-${claim.id}`)?.value,
                    )
                  }
                  className="rounded-xl border border-destructive/30 px-3 py-2 text-sm font-medium text-destructive"
                >
                  {t("admin.refuser")}
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
function AffiliateAdminPanel({ users }) {
  const [affiliates, setAffiliates] = useState([]);
  const [applications, setApplications] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("");
  const load = async () => {
    try {
      const [affiliateResponse, applicationResponse] = await Promise.all([
        api.get("/admin/affiliates"),
        api.get("/admin/affiliate-applications"),
      ]);
      setAffiliates(affiliateResponse.data || []);
      setApplications(applicationResponse.data || []);
    } catch {
      toast.error("Impossible de charger les affiliés.");
    }
  };
  useEffect(() => {
    load();
  }, []);
  const toggle = async (u, enabled) => {
    try {
      await api.put(`/admin/users/${u.id}/affiliate`, { enabled });
      toast.success(
        enabled ? "Mode affilié activé." : "Mode affilié désactivé.",
      );
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur.");
    }
  };
  const detail = async (id) => {
    try {
      setSelected((await api.get(`/admin/affiliates/${id}`)).data);
    } catch {
      toast.error("Impossible de charger le détail.");
    }
  };
  const updateApplication = async (application, status) => {
    try {
      await api.put(`/admin/affiliate-applications/${application.id}`, {
        status,
        note: "",
      });
      toast.success(
        status === "approved"
          ? "Demande acceptée : lien affilié créé."
          : "Demande refusée.",
      );
      load();
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Impossible de traiter la demande.",
      );
    }
  };
  const candidates = users.filter(
    (u) =>
      !u.affiliate_enabled &&
      (!filter ||
        `${u.email} ${u.name}`.toLowerCase().includes(filter.toLowerCase())),
  );
  return (
    <div className="space-y-5">
      <div className="rounded-2xl border bg-card overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b px-5 py-4">
          <div>
            <h2 className="font-head text-xl font-bold">
              {t("admin.demandes_d_affiliation")}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t("admin.valide_une_demande_pour_creer_automatiquement")}
            </p>
          </div>
          <span className="rounded-full bg-amber-500/15 px-2.5 py-1 text-xs font-semibold text-amber-700 dark:text-amber-300">
            {applications.filter((a) => a.status === "pending").length} {t("admin.en_attente")}
          </span>
        </div>
        {applications.length === 0 ? (
          <div className="p-5 text-sm text-muted-foreground">
            {t("admin.aucune_demande_recue")}
          </div>
        ) : (
          applications.map((application) => (
            <div
              key={application.id}
              className="grid gap-3 border-b px-5 py-4 last:border-0 sm:grid-cols-[1.3fr_1fr_auto] sm:items-center"
            >
              <div>
                <div className="font-semibold">
                  {application.name || "Utilisateur"}
                </div>
                <div className="text-xs text-muted-foreground">
                  {application.email} ·{" "}
                  {new Date(application.created_at).toLocaleDateString("fr-FR")}
                </div>
              </div>
              <div className="text-sm text-muted-foreground">
                {application.message || "Aucun message ajouté."}
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`text-xs font-semibold ${application.status === "approved" ? "text-emerald-600" : application.status === "rejected" ? "text-destructive" : "text-amber-600"}`}
                >
                  {application.status}
                </span>
                {application.status === "pending" && (
                  <>
                    <button
                      onClick={() => updateApplication(application, "approved")}
                      className="rounded-lg bg-emerald-600 px-2.5 py-1.5 text-xs font-semibold text-white"
                    >
                      {t("admin.accepter")}
                    </button>
                    <button
                      onClick={() => updateApplication(application, "rejected")}
                      className="rounded-lg border border-destructive/30 px-2.5 py-1.5 text-xs font-semibold text-destructive"
                    >
                      {t("admin.refuser")}
                    </button>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>
      <div className="rounded-2xl border bg-card p-5">
        <h2 className="font-head text-xl font-bold">{t("admin.activer_le_mode_affilie")}</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("admin.choisis_un_utilisateur_il_recevra_son")}
        </p>
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t("admin.rechercher_un_email_ou_un_nom")}
          className="mt-4 w-full rounded-xl border bg-background px-3 py-2"
        />
        <div className="mt-3 max-h-56 overflow-y-auto divide-y">
          {candidates.slice(0, 50).map((u) => (
            <div
              key={u.id}
              className="flex items-center justify-between gap-3 py-2 text-sm"
            >
              <div className="min-w-0">
                <div className="truncate font-medium">{u.name}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {u.email}
                </div>
              </div>
              <button
                onClick={() => toggle(u, true)}
                className="shrink-0 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground"
              >
                {t("admin.activer")}
              </button>
            </div>
          ))}
          {candidates.length === 0 && (
            <div className="py-4 text-sm text-muted-foreground">
              {t("admin.aucun_utilisateur_a_activer")}
            </div>
          )}
        </div>
      </div>
      <div className="rounded-2xl border bg-card overflow-hidden">
        <div className="border-b px-5 py-4">
          <h2 className="font-head text-xl font-bold">{t("admin.affilies_actifs")}</h2>
          <p className="text-sm text-muted-foreground">
            {t("admin.clique_sur_un_affilie_pour_voir")}
          </p>
        </div>
        {affiliates.map((a) => (
          <button
            key={a.id}
            onClick={() => detail(a.id)}
            className={`grid w-full gap-2 border-b px-5 py-4 text-left hover:bg-secondary/50 sm:grid-cols-[1.4fr_0.8fr_0.8fr_0.8fr] ${selected?.affiliate?.id === a.id ? "bg-primary/5" : ""}`}
          >
            <span>
              <span className="font-semibold">{a.name}</span>
              <br />
              <span className="text-xs text-muted-foreground">
                {a.email} · {a.code}
              </span>
            </span>
            <span className="text-sm">
              <MousePointerClick
                size={14}
                className="mr-1 inline text-primary"
              />
              {a.clicks} clics
            </span>
            <span className="text-sm">
              <Users size={14} className="mr-1 inline text-primary" />
              {a.signups} inscrits
            </span>
            <span className="text-sm">
              <WalletCards size={14} className="mr-1 inline text-primary" />
              {a.pending_eur.toFixed(2)} €
            </span>
          </button>
        ))}
        {affiliates.length === 0 && (
          <div className="p-6 text-sm text-muted-foreground">
            {t("admin.aucun_affilie_actif")}
          </div>
        )}
      </div>
      {selected && (
        <div className="rounded-2xl border bg-card p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="font-head text-xl font-bold">
                {t("admin.detail_de")} {selected.affiliate.name}
              </h2>
              <p className="text-sm text-muted-foreground">
                {selected.affiliate.email} · {selected.affiliate.rate * 100}{t("admin.code")} {selected.affiliate.code}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => {
                  const url = `${window.location.origin}/auth?ref=${selected.affiliate.code}`;
                  navigator.clipboard.writeText(url);
                  toast.success("Lien copié.");
                }}
                className="flex items-center gap-1 rounded-lg border px-3 py-2 text-xs font-semibold"
              >
                <Link2 size={14} /> {t("admin.copier_le_lien")}
              </button>
              <button
                onClick={async () => {
                  if (
                    !window.confirm(
                      "Désactiver le mode affilié pour ce compte ? Les anciennes conversions resteront dans l'historique.",
                    )
                  )
                    return;
                  try {
                    await api.put(
                      `/admin/users/${selected.affiliate.id}/affiliate`,
                      { enabled: false },
                    );
                    toast.success("Mode affilié désactivé.");
                    setSelected(null);
                    load();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Erreur.");
                  }
                }}
                className="rounded-lg border border-destructive/30 px-3 py-2 text-xs font-semibold text-destructive"
              >
                {t("admin.desactiver")}
              </button>
            </div>
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <Stat
              icon={MousePointerClick}
              label={t("admin.clics")}
              value={selected.metrics.clicks}
            />
            <Stat
              icon={Users}
              label={t("admin.inscriptions")}
              value={selected.metrics.signups}
            />
            <Stat
              icon={WalletCards}
              label={t("admin.commissions_en_attente")}
              value={`${selected.metrics.pending_eur.toFixed(2)} €`}
            />
          </div>
          <div className="mt-5 divide-y rounded-xl border">
            {selected.conversions.map((c) => (
              <div
                key={c.referred_user_id}
                className="grid gap-1 px-4 py-3 text-sm sm:grid-cols-[1.4fr_0.8fr_0.8fr_1fr]"
              >
                <span>{c.email}</span>
                <span>{c.account_type}</span>
                <span>{c.plan}</span>
                <span>
                  {Number(c.commission_eur || 0).toFixed(2)} € ·{" "}
                  {c.payment_status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
// ==================== Rules panel ====================
function RuleField({ k, min, max, step = 1, r, setR, t }) {
  return (
    <div>
      <label className="text-sm font-medium mb-1 block">
        {t(`admin.rules.${k}`)}
      </label>
      <input
        type="number"
        min={min}
        max={max}
        step={step}
        value={r[k]}
        data-testid={`rule-${k}`}
        onChange={(e) =>
          setR({
            ...r,
            [k]:
              step < 1
                ? parseFloat(e.target.value)
                : parseInt(e.target.value, 10),
          })
        }
        className="w-full px-3 py-2 rounded-xl border bg-background"
      />
    </div>
  );
}
function RulesPanel() {
  const { t } = useTranslation();
  const [r, setR] = useState(null);
  useEffect(() => {
    api.get("/admin/rules").then((res) => setR(res.data));
  }, []);
  if (!r) return <div className="text-muted-foreground">…</div>;
  const save = async () => {
    try {
      await api.put("/admin/rules", r);
      toast.success(t("admin.rules.saved"));
    } catch {
      toast.error("Erreur");
    }
  };
  return (
    <div className="rounded-2xl border bg-card p-5 space-y-4 max-w-xl">
      <h2 className="font-head font-bold text-lg flex items-center gap-2">
        <Settings2 size={18} /> {t("admin.tabs.rules")}
      </h2>
      <div className="grid sm:grid-cols-2 gap-3">
        <RuleField
          k="daily_minutes"
          min={1}
          max={240}
          r={r}
          setR={setR}
          t={t}
        />
        <RuleField
          k="error_threshold"
          min={1}
          max={20}
          r={r}
          setR={setR}
          t={t}
        />
        <RuleField
          k="validations_required"
          min={1}
          max={10}
          r={r}
          setR={setR}
          t={t}
        />
        <RuleField k="arabic_level" min={0} max={10} r={r} setR={setR} t={t} />
        <RuleField
          k="tryhard_probability"
          min={0}
          max={1}
          step={0.05}
          r={r}
          setR={setR}
          t={t}
        />
      </div>
      <div className="border-t pt-4">
        <h3 className="font-semibold mb-1">{t("admin.dosage_des_revisions_du_jour")}</h3>
        <p className="text-xs text-muted-foreground mb-3">
          {t("admin.la_somme_doit_etre_egale_a")}
        </p>
        <div className="grid sm:grid-cols-3 gap-3">
          <RuleField
            k="review_reading_pct"
            min={0}
            max={100}
            r={r}
            setR={setR}
            t={t}
          />
          <RuleField
            k="review_vocabulary_pct"
            min={0}
            max={100}
            r={r}
            setR={setR}
            t={t}
          />
          <RuleField
            k="review_other_pct"
            min={0}
            max={100}
            r={r}
            setR={setR}
            t={t}
          />
        </div>
      </div>
      <button
        data-testid="rule-save"
        onClick={save}
        className="px-5 py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold flex items-center gap-2"
      >
        <Save size={16} /> {t("common.save")}
      </button>
    </div>
  );
}
// ==================== Vocabulary panel ====================
function VocabPanel() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    word_ar: "",
    word_latin: "",
    root: "",
    dialect: "MSA",
    category: "general",
    translation_fr: "",
    translation_en: "",
    example_ar: "",
    example_fr: "",
    example_en: "",
    difficulty: 2,
  });
  const load = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (catFilter) params.set("category", catFilter);
    api.get(`/admin/vocab?${params}`).then((r) => setItems(r.data));
    api.get("/admin/vocab-categories").then((r) => setCategories(r.data));
  };
  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [q, catFilter]);
  const add = async () => {
    if (!form.word_ar && !form.word_latin)
      return toast.error("Mot AR ou latin requis");
    try {
      await api.post("/admin/vocab", form);
      toast.success("Ajouté.");
      setForm({
        ...form,
        word_ar: "",
        word_latin: "",
        root: "",
        translation_fr: "",
        translation_en: "",
        example_ar: "",
        example_fr: "",
        example_en: "",
      });
      load();
    } catch {
      toast.error("Erreur");
    }
  };
  const del = async (id) => {
    await api.delete(`/admin/vocab/${id}`);
    load();
  };
  const uploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/vocab/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Import: ${data.imported} lignes, ${data.errors} erreurs`);
      load();
    } catch {
      toast.error("Import échoué");
    }
    e.target.value = "";
  };
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border bg-card p-5">
        <h3 className="font-head font-bold mb-3 flex items-center gap-2">
          <Plus size={16} /> {t("admin.ajouter_un_mot")}
        </h3>
        <div className="grid sm:grid-cols-2 gap-2">
          <input
            placeholder={t("admin.mot_arabe")}
            dir="rtl"
            value={form.word_ar}
            onChange={(e) => setForm({ ...form, word_ar: e.target.value })}
            className="px-3 py-2 rounded-xl border bg-background arabic"
            data-testid="vocab-word-ar"
          />
          <input
            placeholder={t("admin.mot_latin_bayt")}
            value={form.word_latin}
            onChange={(e) => setForm({ ...form, word_latin: e.target.value })}
            className="px-3 py-2 rounded-xl border bg-background"
            data-testid="vocab-word-latin"
          />
          <input
            placeholder={t("admin.racine_b_y_t")}
            value={form.root}
            onChange={(e) => setForm({ ...form, root: e.target.value })}
            className="px-3 py-2 rounded-xl border bg-background"
            data-testid="vocab-root"
          />
          <select
            value={form.dialect}
            onChange={(e) => setForm({ ...form, dialect: e.target.value })}
            className="px-3 py-2 rounded-xl border bg-background"
          >
            {[
              "MSA",
              "darija-maroc",
              "darija-algerie",
              "egyptien",
              "levantin",
            ].map((d) => (
              <option key={d}>{d}</option>
            ))}
          </select>
          <input
            placeholder={t("admin.categorie_nourriture_famille_transport")}
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            list="cat-list"
            className="px-3 py-2 rounded-xl border bg-background"
            data-testid="vocab-category"
          />
          <datalist id="cat-list">
            {categories.map((c) => (
              <option key={c.category} value={c.category}>
                {c.count} mots
              </option>
            ))}
          </datalist>
          <input
            type="number"
            min={1}
            max={5}
            placeholder={t("admin.difficulte_1_5")}
            value={form.difficulty}
            onChange={(e) =>
              setForm({
                ...form,
                difficulty: parseInt(e.target.value, 10) || 2,
              })
            }
            className="px-3 py-2 rounded-xl border bg-background"
          />
          <input
            placeholder={t("admin.traduction_fr")}
            value={form.translation_fr}
            onChange={(e) =>
              setForm({ ...form, translation_fr: e.target.value })
            }
            className="px-3 py-2 rounded-xl border bg-background"
            data-testid="vocab-fr"
          />
          <input
            placeholder={t("admin.traduction_en")}
            value={form.translation_en}
            onChange={(e) =>
              setForm({ ...form, translation_en: e.target.value })
            }
            className="px-3 py-2 rounded-xl border bg-background"
          />
          <input
            placeholder={t("admin.phrase_exemple_ar")}
            dir="rtl"
            value={form.example_ar}
            onChange={(e) => setForm({ ...form, example_ar: e.target.value })}
            className="px-3 py-2 rounded-xl border bg-background arabic sm:col-span-2"
          />
          <input
            placeholder={t("admin.phrase_exemple_fr")}
            value={form.example_fr}
            onChange={(e) => setForm({ ...form, example_fr: e.target.value })}
            className="px-3 py-2 rounded-xl border bg-background"
          />
          <input
            placeholder={t("admin.phrase_exemple_en")}
            value={form.example_en}
            onChange={(e) => setForm({ ...form, example_en: e.target.value })}
            className="px-3 py-2 rounded-xl border bg-background"
          />
        </div>
        <div className="flex gap-2 mt-3 flex-wrap">
          <button
            data-testid="vocab-add"
            onClick={add}
            className="px-4 py-2 rounded-xl bg-primary text-primary-foreground font-semibold"
          >
            {t("admin.ajouter")}
          </button>
          <label className="px-4 py-2 rounded-xl border font-medium cursor-pointer flex items-center gap-2 hover:bg-secondary">
            <Upload size={16} /> {t("admin.import_csv")}
            <input
              type="file"
              accept=".csv"
              onChange={uploadCsv}
              className="hidden"
              data-testid="vocab-csv"
            />
          </label>
          <TemplateButton type="vocab" label={t("admin.template_csv_simple")} />
          <label className="px-4 py-2 rounded-xl border font-medium cursor-pointer flex items-center gap-2 hover:bg-secondary">
            <Upload size={16} /> {t("admin.import_csv_multi_dialectes")}
            <input
              type="file"
              accept=".csv"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                const fd = new FormData();
                fd.append("file", f);
                try {
                  const { data } = await api.post(
                    "/admin/vocab/import-multidialect",
                    fd,
                    { headers: { "Content-Type": "multipart/form-data" } },
                  );
                  toast.success(
                    `Import : ${data.imported} entrées, ${data.errors} erreurs`,
                  );
                  load();
                } catch {
                  toast.error("Import échoué");
                }
                e.target.value = "";
              }}
              className="hidden"
            />
          </label>
          <TemplateButton
            type="vocab-multidialect"
            label={t("admin.template_multi_dialectes")}
          />
        </div>
        <div className="text-xs text-muted-foreground mt-2">
          {t("admin.colonnes_csv")}{" "}
          <code>
            {t("admin.mot_racine_dialecte_traduction_fr_traduction")}
          </code>{" "}
          {t("admin.aussi")} <code>{t("admin.word_latin_exemple_fr_exemple_en")}</code>).
        </div>
      </div>
      <div className="rounded-2xl border bg-card p-5">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <input
            placeholder={t("navbar.rechercher_2")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="flex-1 min-w-[180px] px-3 py-2 rounded-xl border bg-background"
          />
          <select
            value={catFilter}
            onChange={(e) => setCatFilter(e.target.value)}
            className="px-3 py-2 rounded-xl border bg-background"
          >
            <option value="">{t("admin.toutes_categories")}</option>
            {categories.map((c) => (
              <option key={c.category} value={c.category}>
                {c.category} ({c.count})
              </option>
            ))}
          </select>
          <button onClick={load} className="p-2 rounded-lg border">
            <RefreshCw size={14} />
          </button>
          <span className="text-sm text-muted-foreground">
            {items.length} mots
          </span>
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {items.map((v) => (
            <div
              key={v.id}
              className="flex items-center justify-between py-1.5 border-b last:border-0 text-sm gap-3"
            >
              <div className="flex-1 min-w-0 truncate">
                <span className="arabic font-bold text-base mr-2" dir="rtl">
                  {v.word_ar}
                </span>
                <span className="italic text-muted-foreground">
                  {v.word_latin}
                </span>
                <span className="mx-2">→</span>
                <span>{v.translation_fr}</span>
              </div>
              <span className="text-xs px-2 py-0.5 rounded-full bg-secondary">
                {v.dialect}
              </span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-ocre/15 text-ocre">
                {v.category || "general"}
              </span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                D{v.difficulty}
              </span>
              <button
                data-testid={`vocab-del-${v.id}`}
                onClick={() => del(v.id)}
                className="text-destructive p-1"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
// ==================== Verbs panel ====================
function VerbsPanel() {
  const [items, setItems] = useState([]);
  const [tense, setTense] = useState("");
  const [form, setForm] = useState({
    root: "",
    verb_ar: "",
    verb_latin: "",
    meaning_fr: "",
    meaning_en: "",
    form: "I",
    tense: "past",
    person: "3ms",
    conjugation_ar: "",
    dialect: "MSA",
    arabic_level: 3,
    difficulty: 2,
  });
  const load = () =>
    api
      .get(`/admin/verbs${tense ? `?tense=${tense}` : ""}`)
      .then((r) => setItems(r.data));
  useEffect(() => {
    load();
  }, [tense]);
  const add = async () => {
    if (!form.verb_ar && !form.conjugation_ar)
      return toast.error("Verbe requis");
    try {
      await api.post("/admin/verbs", form);
      toast.success("Ajouté.");
      setForm({
        ...form,
        root: "",
        verb_ar: "",
        verb_latin: "",
        meaning_fr: "",
        meaning_en: "",
        conjugation_ar: "",
      });
      load();
    } catch {
      toast.error("Erreur");
    }
  };
  const del = async (id) => {
    await api.delete(`/admin/verbs/${id}`);
    load();
  };
  const uploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/verbs/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Import: ${data.imported}, erreurs: ${data.errors}`);
      load();
    } catch {
      toast.error("Import échoué");
    }
    e.target.value = "";
  };
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border bg-card p-5">
        <h3 className="font-head font-bold mb-3 flex items-center gap-2">
          <Plus size={16} /> {t("admin.ajouter_un_verbe_conjugue")}
        </h3>
        <div className="grid sm:grid-cols-3 gap-2">
          <input
            placeholder={t("admin.racine_k_t_b")}
            value={form.root}
            onChange={(e) => setForm({ ...form, root: e.target.value })}
            className="px-3 py-2 rounded-xl border bg-background"
          />
          <input
            placeholder={t("admin.verbe_ar")}
            dir="rtl"
            value={form.verb_ar}
            onChange={(e) => setForm({ ...form, verb_ar: e.target.value })}
            className="px-3 py-2 rounded-xl border bg-background arabic"
          />
          <input
            placeholder={t("admin.verbe_latin_kataba")}
            value={form.verb_latin}
            onChange={(e) => setForm({ ...form, verb_latin: e.target.value })}
            className="px-3 py-2 rounded-xl border bg-background"
          />
          <input
            placeholder={t("admin.sens_fr")}
            value={form.meaning_fr}
            onChange={(e) => setForm({ ...form, meaning_fr: e.target.value })}
            className="px-3 py-2 rounded-xl border bg-background"
          />
          <input
            placeholder={t("admin.sens_en")}
            value={form.meaning_en}
            onChange={(e) => setForm({ ...form, meaning_en: e.target.value })}
            className="px-3 py-2 rounded-xl border bg-background"
          />
          <select
            value={form.form}
            onChange={(e) => setForm({ ...form, form: e.target.value })}
            className="px-3 py-2 rounded-xl border bg-background"
          >
            {["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"].map(
              (f) => (
                <option key={f}>{f}</option>
              ),
            )}
          </select>
          <select
            value={form.tense}
            onChange={(e) => setForm({ ...form, tense: e.target.value })}
            className="px-3 py-2 rounded-xl border bg-background"
          >
            {[
              "past",
              "present",
              "imperative",
              "future",
              "jussive",
              "subjunctive",
            ].map((f) => (
              <option key={f}>{f}</option>
            ))}
          </select>
          <select
            value={form.person}
            onChange={(e) => setForm({ ...form, person: e.target.value })}
            className="px-3 py-2 rounded-xl border bg-background"
          >
            {[
              "1sg",
              "2ms",
              "2fs",
              "3ms",
              "3fs",
              "1pl",
              "2mp",
              "3mp",
              "3fp",
            ].map((f) => (
              <option key={f}>{f}</option>
            ))}
          </select>
          <input
            placeholder={t("admin.conjugaison_ar")}
            dir="rtl"
            value={form.conjugation_ar}
            onChange={(e) =>
              setForm({ ...form, conjugation_ar: e.target.value })
            }
            className="px-3 py-2 rounded-xl border bg-background arabic"
          />
          <div>
            <label className="text-xs text-muted-foreground">
              {t("admin.niveau_arabe_0_10")}
            </label>
            <input
              type="number"
              min={0}
              max={10}
              value={form.arabic_level}
              onChange={(e) =>
                setForm({
                  ...form,
                  arabic_level: parseInt(e.target.value, 10) || 0,
                })
              }
              className="w-full px-3 py-2 rounded-xl border bg-background"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">
              {t("admin.difficulte_1_5")}
            </label>
            <input
              type="number"
              min={1}
              max={5}
              value={form.difficulty}
              onChange={(e) =>
                setForm({
                  ...form,
                  difficulty: parseInt(e.target.value, 10) || 2,
                })
              }
              className="w-full px-3 py-2 rounded-xl border bg-background"
            />
          </div>
        </div>
        <div className="flex gap-2 mt-3 flex-wrap">
          <button
            data-testid="verb-add"
            onClick={add}
            className="px-4 py-2 rounded-xl bg-primary text-primary-foreground font-semibold"
          >
            {t("admin.ajouter")}
          </button>
          <label className="px-4 py-2 rounded-xl border font-medium cursor-pointer flex items-center gap-2 hover:bg-secondary">
            <Upload size={16} /> {t("admin.import_csv")}
            <input
              type="file"
              accept=".csv"
              onChange={uploadCsv}
              className="hidden"
              data-testid="verbs-csv"
            />
          </label>
          <TemplateButton type="verbs" label={t("admin.template_csv")} />
        </div>
        <div className="text-xs text-muted-foreground mt-2">
          {t("admin.colonnes_csv")}{" "}
          <code>
            {t("admin.racine_verbe_ar_verbe_latin_sens")}
          </code>
          .
        </div>
      </div>
      <div className="rounded-2xl border bg-card p-5">
        <div className="flex items-center gap-2 mb-3">
          <select
            value={tense}
            onChange={(e) => setTense(e.target.value)}
            className="px-3 py-2 rounded-xl border bg-background"
          >
            <option value="">{t("admin.tous_temps")}</option>
            {[
              "past",
              "present",
              "imperative",
              "future",
              "jussive",
              "subjunctive",
            ].map((f) => (
              <option key={f}>{f}</option>
            ))}
          </select>
          <span className="text-sm text-muted-foreground">
            {items.length} verbes
          </span>
        </div>
        <div className="max-h-[380px] overflow-y-auto text-sm">
          {items.map((v) => (
            <div
              key={v.id}
              className="flex items-center justify-between py-1.5 border-b last:border-0 gap-3"
            >
              <div className="flex-1 truncate">
                <span className="arabic font-bold" dir="rtl">
                  {v.conjugation_ar || v.verb_ar}
                </span>
                <span className="italic text-muted-foreground ml-2">
                  {v.verb_latin}
                </span>
                <span className="mx-2">→</span>
                <span>{v.meaning_fr}</span>
                <span className="ml-2 text-xs text-muted-foreground">
                  ({v.tense} · {v.person})
                </span>
              </div>
              <span className="text-xs px-2 py-0.5 rounded-full bg-secondary">
                N{v.arabic_level}
              </span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                D{v.difficulty}
              </span>
              <button
                data-testid={`verb-del-${v.id}`}
                onClick={() => del(v.id)}
                className="text-destructive p-1"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
// ==================== Questions panel ====================
function QuestionsPanel() {
  const [items, setItems] = useState([]);
  const [skill, setSkill] = useState("");
  const [form, setForm] = useState({
    type: "mcq",
    skill: "grammar",
    prompt_fr: "",
    prompt_en: "",
    options_fr: ["", "", "", ""],
    options_en: ["", "", "", ""],
    correct_index: 0,
    difficulty: 2,
  });
  const load = () =>
    api
      .get(`/admin/questions${skill ? `?skill=${skill}` : ""}`)
      .then((r) => setItems(r.data));
  useEffect(() => {
    load();
  }, [skill]);
  const add = async () => {
    if (!form.prompt_fr) return toast.error("Prompt FR requis");
    try {
      await api.post("/admin/questions", {
        ...form,
        options_fr: form.options_fr.filter((o) => o),
        options_en: form.options_en.filter((o) => o),
      });
      toast.success("Ajoutée.");
      setForm({
        ...form,
        prompt_fr: "",
        prompt_en: "",
        options_fr: ["", "", "", ""],
        options_en: ["", "", "", ""],
        correct_index: 0,
      });
      load();
    } catch {
      toast.error("Erreur");
    }
  };
  const del = async (id) => {
    await api.delete(`/admin/questions/${id}`);
    load();
  };
  const setOpt = (which, i, v) =>
    setForm({ ...form, [which]: form[which].map((x, j) => (j === i ? v : x)) });
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border bg-card p-5">
        <h3 className="font-head font-bold mb-3 flex items-center gap-2">
          <Plus size={16} /> {t("lessoneditor.ajouter_une_question")}
        </h3>
        <div className="grid sm:grid-cols-3 gap-2 mb-2">
          <select
            value={form.skill}
            onChange={(e) => setForm({ ...form, skill: e.target.value })}
            className="px-3 py-2 rounded-xl border bg-background"
          >
            {[
              "grammar",
              "vocabulary",
              "conjugation",
              "alphabet",
              "reading",
              "writing",
            ].map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
          <div>
            <label className="text-xs text-muted-foreground">
              {t("lessoneditor.difficulte_1_5")}
            </label>
            <input
              type="number"
              min={1}
              max={5}
              value={form.difficulty}
              onChange={(e) =>
                setForm({
                  ...form,
                  difficulty: parseInt(e.target.value, 10) || 2,
                })
              }
              className="w-full px-3 py-2 rounded-xl border bg-background"
            />
          </div>
          <select
            value={form.correct_index}
            onChange={(e) =>
              setForm({ ...form, correct_index: parseInt(e.target.value, 10) })
            }
            className="px-3 py-2 rounded-xl border bg-background"
          >
            {[0, 1, 2, 3].map((i) => (
              <option key={i} value={i}>
                {t("admin.bonne_reponse_option")} {i + 1}
              </option>
            ))}
          </select>
        </div>
        <input
          placeholder={t("lessoneditor.question_fr")}
          value={form.prompt_fr}
          onChange={(e) => setForm({ ...form, prompt_fr: e.target.value })}
          className="w-full px-3 py-2 rounded-xl border bg-background mb-2"
          data-testid="q-prompt-fr"
        />
        <input
          placeholder={t("lessoneditor.question_en")}
          value={form.prompt_en}
          onChange={(e) => setForm({ ...form, prompt_en: e.target.value })}
          className="w-full px-3 py-2 rounded-xl border bg-background mb-2"
        />
        <div className="grid sm:grid-cols-2 gap-2">
          <div>
            <div className="text-xs text-muted-foreground mb-1">{t("lessoneditor.options_fr")}</div>
            {form.options_fr.map((o, i) => (
              <input
                key={i}
                placeholder={`Option ${i + 1}`}
                value={o}
                onChange={(e) => setOpt("options_fr", i, e.target.value)}
                className="w-full px-3 py-2 rounded-xl border bg-background mb-1"
              />
            ))}
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">{t("lessoneditor.options_en")}</div>
            {form.options_en.map((o, i) => (
              <input
                key={i}
                placeholder={`Option ${i + 1}`}
                value={o}
                onChange={(e) => setOpt("options_en", i, e.target.value)}
                className="w-full px-3 py-2 rounded-xl border bg-background mb-1"
              />
            ))}
          </div>
        </div>
        <button
          data-testid="q-add"
          onClick={add}
          className="mt-3 px-4 py-2 rounded-xl bg-primary text-primary-foreground font-semibold"
        >
          {t("admin.ajouter")}
        </button>
      </div>
      <div className="rounded-2xl border bg-card p-5">
        <div className="flex items-center gap-2 mb-3">
          <select
            value={skill}
            onChange={(e) => setSkill(e.target.value)}
            className="px-3 py-2 rounded-xl border bg-background"
          >
            <option value="">{t("admin.toutes_competences")}</option>
            {[
              "grammar",
              "vocabulary",
              "conjugation",
              "alphabet",
              "reading",
              "writing",
            ].map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
          <span className="text-sm text-muted-foreground">
            {items.length} questions
          </span>
        </div>
        <div className="max-h-[380px] overflow-y-auto text-sm">
          {items.map((v) => (
            <div
              key={v.id}
              className="flex items-center justify-between py-1.5 border-b last:border-0 gap-3"
            >
              <div className="flex-1 truncate">{v.prompt_fr}</div>
              <span className="text-xs px-2 py-0.5 rounded-full bg-secondary">
                {v.skill}
              </span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                D{v.difficulty}
              </span>
              <button
                data-testid={`q-del-${v.id}`}
                onClick={() => del(v.id)}
                className="text-destructive p-1"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
// ==================== Daily Plan builder (template unique, drag & drop) ====================
function DailyPlanBuilder() {
  const nav = useNavigate();
  const [plan, setPlan] = useState({ items: [], name: "Programme principal" });
  const [lessons, setLessons] = useState([]);
  const [staticLessons, setStaticLessons] = useState([]);
  const [vocab, setVocab] = useState([]);
  const [verbs, setVerbs] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [filter, setFilter] = useState("");
  const [tab, setTab] = useState("lessons");
  const load = () =>
    api.get("/admin/daily-plan-template").then((r) =>
      setPlan({
        items: r.data.items || [],
        name: r.data.name || "Programme principal",
      }),
    );
  useEffect(() => {
    load();
    api.get("/admin/lessons?limit=200").then((r) => setLessons(r.data));
    api.get("/admin/vocab?limit=200").then((r) => setVocab(r.data));
    api.get("/admin/verbs?limit=200").then((r) => setVerbs(r.data));
    api.get("/admin/questions?limit=200").then((r) => setQuestions(r.data));
    // leçons statiques via content/themes
    api.get("/content/themes").then((r) => {
      const flat = [];
      (r.data || []).forEach((th) =>
        (th.lessons || []).forEach((l) =>
          flat.push({
            id: l.id,
            title: l.title,
            theme: th.title,
            skill: th.skill,
            minutes: l.minutes,
          }),
        ),
      );
      setStaticLessons(flat);
    });
  }, []);
  const addItem = (type, ref, label, minutes = 2) => {
    // insertion en BAS de la liste (ligne du bas vers le haut : items[0] = premier à faire)
    const it = {
      type,
      ref_id: ref,
      order: plan.items.length,
      label_fr: label,
      label_en: label,
      minutes,
    };
    setPlan({ ...plan, items: [...plan.items, it] });
  };
  const removeItem = (i) =>
    setPlan({ ...plan, items: plan.items.filter((_, j) => j !== i) });
  const onDragEnd = (r) => {
    if (!r.destination) return;
    const items = Array.from(plan.items);
    const [moved] = items.splice(r.source.index, 1);
    items.splice(r.destination.index, 0, moved);
    setPlan({ ...plan, items });
  };
  const save = async () => {
    try {
      await api.put("/admin/daily-plan-template", plan);
      toast.success("Template enregistré.");
    } catch {
      toast.error("Erreur d'enregistrement.");
    }
  };
  const resetDefault = async () => {
    if (
      !confirm(
        "Réinitialiser le template avec toutes les leçons existantes ? (les items actuels seront écrasés)",
      )
    )
      return;
    try {
      const { data } = await api.post(
        "/admin/daily-plan-template/reset-default",
      );
      toast.success(`Template régénéré (${data.count} items).`);
      load();
    } catch {
      toast.error("Erreur.");
    }
  };
  const filtered = (arr, kf) =>
    arr.filter(
      (x) =>
        !filter || (kf(x) || "").toLowerCase().includes(filter.toLowerCase()),
    );
  const SRC_TABS = [
    ["lessons", "Leçons statiques", staticLessons.length],
    ["dblessons", "Leçons DB", lessons.length],
    ["questions", "Questions", questions.length],
    ["vocab", "Vocabulaire", vocab.length],
    ["verbs", "Verbes", verbs.length],
  ];
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border bg-card p-4 flex items-center gap-3 flex-wrap">
        <span className="text-sm font-medium">
          {t("admin.programme_principal_ligne_unique_du_haut")}
        </span>
        <input
          placeholder={t("lettercard.nom")}
          value={plan.name}
          onChange={(e) => setPlan({ ...plan, name: e.target.value })}
          className="px-3 py-2 rounded-xl border bg-background"
        />
        <div className="flex-1" />
        <button
          data-testid="plan-reset-default"
          onClick={resetDefault}
          className="px-4 py-2 rounded-xl border font-medium flex items-center gap-2"
        >
          <RefreshCw size={16} /> {t("admin.reinitialiser_auto")}
        </button>
        <button
          onClick={() => nav("/daily-plan")}
          className="px-4 py-2 rounded-xl border font-medium flex items-center gap-2"
          title={t("admin.tester_la_session_comme_un_utilisateur")}
        >
          {t("admin.tester_la_session")}
        </button>
        <button
          data-testid="plan-save"
          onClick={save}
          className="px-4 py-2 rounded-xl bg-primary text-primary-foreground font-semibold flex items-center gap-2"
        >
          <Save size={16} /> {t("lessoneditor.enregistrer")}
        </button>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Sources */}
        <div className="rounded-2xl border bg-card p-4">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <h3 className="font-head font-bold">{t("admin.bibliotheque")}</h3>
            <input
              placeholder={t("admin.filtrer")}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="ml-auto px-2 py-1 rounded-lg border bg-background text-sm"
            />
          </div>
          <div className="flex gap-1 mb-3 flex-wrap">
            {SRC_TABS.map(([k, l, n]) => (
              <button
                key={k}
                onClick={() => setTab(k)}
                className={`px-2.5 py-1 rounded-lg text-xs border ${tab === k ? "bg-primary text-primary-foreground border-primary" : "hover:bg-secondary"}`}
              >
                {l} ({n})
              </button>
            ))}
          </div>
          <div className="space-y-1 max-h-[520px] overflow-y-auto">
            {tab === "lessons" &&
              filtered(staticLessons, (x) => x.title)
                .slice(0, 60)
                .map((l) => (
                  <button
                    key={l.id}
                    onClick={() =>
                      addItem("lesson", l.id, l.title, l.minutes || 4)
                    }
                    data-testid={`src-lesson-${l.id}`}
                    className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-secondary flex items-center gap-2"
                  >
                    <Plus size={12} />{" "}
                    <span className="flex-1 truncate">{l.title}</span>
                    <span className="text-xs opacity-60">
                      {l.theme} · {l.minutes}min
                    </span>
                  </button>
                ))}
            {tab === "dblessons" &&
              filtered(lessons, (x) => x.title_fr + " " + x.title_en)
                .slice(0, 60)
                .map((l) => (
                  <button
                    key={l.id}
                    onClick={() =>
                      addItem("lesson", l.id, l.title_fr, l.minutes || 4)
                    }
                    className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-secondary flex items-center gap-2"
                  >
                    <Plus size={12} />{" "}
                    <span className="flex-1 truncate">{l.title_fr}</span>
                    <span className="text-xs opacity-60">
                      {l.skill} · {l.minutes}min
                    </span>
                  </button>
                ))}
            {tab === "questions" &&
              filtered(questions, (x) => x.prompt_fr)
                .slice(0, 60)
                .map((q) => (
                  <button
                    key={q.id}
                    onClick={() =>
                      addItem("question", q.id, q.prompt_fr.slice(0, 60))
                    }
                    data-testid={`src-q-${q.id}`}
                    className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-secondary flex items-center gap-2"
                  >
                    <Plus size={12} />{" "}
                    <span className="flex-1 truncate">{q.prompt_fr}</span>
                    <span className="text-xs opacity-60">D{q.difficulty}</span>
                  </button>
                ))}
            {tab === "vocab" &&
              filtered(vocab, (x) => x.word_latin + " " + x.translation_fr)
                .slice(0, 60)
                .map((v) => (
                  <button
                    key={v.id}
                    onClick={() =>
                      addItem(
                        "vocab",
                        v.id,
                        `${v.word_latin} — ${v.translation_fr}`,
                        1,
                      )
                    }
                    className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-secondary flex items-center gap-2"
                  >
                    <Plus size={12} />{" "}
                    <span className="flex-1 truncate">
                      {v.word_latin} — {v.translation_fr}
                    </span>
                    <span className="text-xs opacity-60">{v.dialect}</span>
                  </button>
                ))}
            {tab === "verbs" &&
              filtered(verbs, (x) => x.verb_latin + " " + x.meaning_fr)
                .slice(0, 60)
                .map((v) => (
                  <button
                    key={v.id}
                    onClick={() =>
                      addItem(
                        "verb",
                        v.id,
                        `${v.meaning_fr} — ${v.tense}·${v.person}`,
                        1,
                      )
                    }
                    className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-secondary flex items-center gap-2"
                  >
                    <Plus size={12} />{" "}
                    <span className="flex-1 truncate">
                      {v.meaning_fr} — {v.tense}·{v.person}
                    </span>
                  </button>
                ))}
          </div>
        </div>
        {/* Plan */}
        <div className="rounded-2xl border bg-card p-4">
          <h3 className="font-head font-bold mb-3">
            {t("admin.programme")}{plan.items.length} {t("admin.items")}
          </h3>
          <p className="text-xs text-muted-foreground mb-2">
            {t("admin.ordre_ordre_de_presentation_a_l")}
          </p>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="plan">
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="min-h-[400px] max-h-[520px] overflow-y-auto space-y-1"
                >
                  {plan.items.map((it, i) => (
                    <Draggable
                      key={`${it.type}-${it.ref_id}-${i}`}
                      draggableId={`${it.type}-${it.ref_id}-${i}`}
                      index={i}
                    >
                      {(prov) => (
                        <div
                          ref={prov.innerRef}
                          {...prov.draggableProps}
                          className="flex items-center gap-2 rounded-xl border bg-background px-2 py-2 text-sm"
                        >
                          <span className="text-xs w-6 shrink-0 text-muted-foreground">
                            {i + 1}
                          </span>
                          <span
                            {...prov.dragHandleProps}
                            className="cursor-grab shrink-0"
                          >
                            <GripVertical size={14} />
                          </span>
                          <span
                            className={`text-xs uppercase px-2 py-0.5 rounded-full font-semibold shrink-0 ${it.type === "lesson" ? "bg-primary text-primary-foreground" : "bg-secondary"}`}
                          >
                            {it.type}
                          </span>
                          <span className="flex-1 truncate">{it.label_fr}</span>
                          <span className="text-xs opacity-60 shrink-0">
                            {it.minutes}m
                          </span>
                          <button
                            onClick={() => removeItem(i)}
                            className="text-destructive p-1 shrink-0"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                  {plan.items.length === 0 && (
                    <div className="text-sm text-muted-foreground text-center py-8">
                      {t("admin.utilise_reinitialiser_auto_pour_partir_sur")}
                    </div>
                  )}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      </div>
    </div>
  );
}
// ==================== Lessons panel (CRUD + CSV bilingue + éditeur complet) ====================
function LessonsPanel() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [levelFilter, setLevelFilter] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    title_fr: "",
    title_en: "",
    term_ar: "",
    translit: "",
    minutes: 4,
    skill: "grammar",
    level: "A1",
    order: 0,
    objective_fr: "",
    objective_en: "",
  });
  const load = () =>
    api
      .get(`/admin/lessons${q ? `?q=${encodeURIComponent(q)}` : ""}`)
      .then((r) => setItems(r.data));
  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [q]);
  const filtered = items.filter((x) => !levelFilter || x.level === levelFilter);
  const add = async () => {
    if (!form.title_fr && !form.title_en) return toast.error("Titre requis");
    try {
      const { data } = await api.post("/admin/lessons", {
        ...form,
        blocks_fr: [],
        blocks_en: [],
        quiz: [],
      });
      toast.success(
        "Leçon créée. Ouvre l'éditeur pour ajouter contenu et questions.",
      );
      setForm({
        ...form,
        title_fr: "",
        title_en: "",
        term_ar: "",
        translit: "",
        objective_fr: "",
        objective_en: "",
      });
      load();
      // ouvre l'éditeur sur la nouvelle leçon
      if (data.id) setEditingId(data.id);
    } catch {
      toast.error("Erreur");
    }
  };
  const del = async (id) => {
    if (!confirm("Supprimer la leçon ?")) return;
    await api.delete(`/admin/lessons/${id}`);
    load();
  };
  const uploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/lessons/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Import : ${data.imported} leçons, ${data.errors} erreurs`);
      load();
    } catch {
      toast.error("Import échoué");
    }
    e.target.value = "";
  };
  const uploadQuestionsCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/questions/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(
        `Questions importées : ${data.imported}, erreurs : ${data.errors}`,
      );
    } catch {
      toast.error("Import échoué");
    }
    e.target.value = "";
  };
  const LEVELS = ["base", "A1", "A2", "B1", "B2", "C1", "C2"];
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border bg-card p-5">
        <h3 className="font-head font-bold mb-3 flex items-center gap-2">
          <Plus size={16} /> {t("admin.ajouter_une_lecon")}
        </h3>
        <div className="grid sm:grid-cols-2 gap-2">
          <input
            placeholder={t("lessoneditor.titre_fr")}
            value={form.title_fr}
            onChange={(e) => setForm({ ...form, title_fr: e.target.value })}
            className="px-3 py-2 rounded-xl border bg-background"
            data-testid="lesson-title-fr"
          />
          <input
            placeholder={t("lessoneditor.title_en")}
            value={form.title_en}
            onChange={(e) => setForm({ ...form, title_en: e.target.value })}
            className="px-3 py-2 rounded-xl border bg-background"
          />
          <input
            placeholder={t("admin.terme_ar_facultatif")}
            dir="rtl"
            value={form.term_ar}
            onChange={(e) => setForm({ ...form, term_ar: e.target.value })}
            className="px-3 py-2 rounded-xl border bg-background arabic"
          />
          <input
            placeholder={t("lessoneditor.translitteration")}
            value={form.translit}
            onChange={(e) => setForm({ ...form, translit: e.target.value })}
            className="px-3 py-2 rounded-xl border bg-background"
          />
          <select
            value={form.skill}
            onChange={(e) => setForm({ ...form, skill: e.target.value })}
            className="px-3 py-2 rounded-xl border bg-background"
          >
            {[
              "grammar",
              "vocabulary",
              "conjugation",
              "alphabet",
              "reading",
              "writing",
            ].map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
          <select
            value={form.level}
            onChange={(e) => setForm({ ...form, level: e.target.value })}
            className="px-3 py-2 rounded-xl border bg-background"
          >
            {LEVELS.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
          <input
            type="number"
            min={1}
            max={60}
            value={form.minutes}
            onChange={(e) =>
              setForm({ ...form, minutes: parseInt(e.target.value, 10) || 4 })
            }
            placeholder={t("admin.minutes")}
            className="px-3 py-2 rounded-xl border bg-background"
          />
          <input
            type="number"
            value={form.order}
            onChange={(e) =>
              setForm({ ...form, order: parseInt(e.target.value, 10) || 0 })
            }
            placeholder={t("admin.ordre")}
            className="px-3 py-2 rounded-xl border bg-background"
          />
          <input
            placeholder={t("lessoneditor.objectif_fr")}
            value={form.objective_fr}
            onChange={(e) => setForm({ ...form, objective_fr: e.target.value })}
            className="px-3 py-2 rounded-xl border bg-background"
          />
          <input
            placeholder={t("lessoneditor.objective_en")}
            value={form.objective_en}
            onChange={(e) => setForm({ ...form, objective_en: e.target.value })}
            className="px-3 py-2 rounded-xl border bg-background"
          />
        </div>
        <div className="flex gap-2 mt-3 flex-wrap">
          <button
            data-testid="lesson-add"
            onClick={add}
            className="px-4 py-2 rounded-xl bg-primary text-primary-foreground font-semibold"
          >
            {t("admin.creer_editer")}
          </button>
          <label className="px-4 py-2 rounded-xl border font-medium cursor-pointer flex items-center gap-2 hover:bg-secondary">
            <Upload size={16} /> {t("admin.import_csv_lecons")}
            <input
              type="file"
              accept=".csv"
              onChange={uploadCsv}
              className="hidden"
              data-testid="lessons-csv"
            />
          </label>
          <TemplateButton type="lessons" label={t("admin.template_lecons")} />
          <label className="px-4 py-2 rounded-xl border font-medium cursor-pointer flex items-center gap-2 hover:bg-secondary">
            <Upload size={16} /> {t("admin.import_csv_questions")}
            <input
              type="file"
              accept=".csv"
              onChange={uploadQuestionsCsv}
              className="hidden"
              data-testid="questions-csv"
            />
          </label>
          <TemplateButton type="questions" label={t("admin.template_questions")} />
        </div>
      </div>
      <div className="rounded-2xl border bg-card p-5">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <input
            placeholder={t("navbar.rechercher_2")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="flex-1 min-w-[200px] px-3 py-2 rounded-xl border bg-background"
          />
          <select
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value)}
            className="px-3 py-2 rounded-xl border bg-background"
          >
            <option value="">{t("admin.tous_niveaux")}</option>
            {LEVELS.map((l) => (
              <option key={l}>{l}</option>
            ))}
          </select>
          <span className="text-sm text-muted-foreground">
            {filtered.length} / {items.length} {t("admin.lecon_s")}
          </span>
        </div>
        <div className="max-h-[420px] overflow-y-auto space-y-1">
          {filtered.map((v) => (
            <div
              key={v.id}
              className="flex items-center justify-between py-2 px-2 border rounded-lg text-sm gap-3 hover:bg-secondary/40"
            >
              <button
                onClick={() => setEditingId(v.id)}
                className="flex-1 min-w-0 text-left truncate hover:text-primary"
              >
                <span className="font-medium">{v.title_fr}</span>
                {v.title_en && (
                  <span className="text-muted-foreground"> · {v.title_en}</span>
                )}
              </button>
              <span className="text-xs px-2 py-0.5 rounded-full bg-secondary">
                {v.skill}
              </span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                {v.level || "A1"}
              </span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-secondary">
                {v.minutes}min
              </span>
              <span className="text-xs text-muted-foreground">
                {(v.quiz || []).length}q
              </span>
              <button
                onClick={() => setEditingId(v.id)}
                className="text-primary p-1 text-xs hover:underline"
              >
                {t("admin.editer")}
              </button>
              <button
                data-testid={`lesson-del-${v.id}`}
                onClick={() => del(v.id)}
                className="text-destructive p-1"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>
      {editingId && (
        <LessonEditor
          lessonId={editingId}
          onClose={() => setEditingId(null)}
          onSaved={() => {
            load();
          }}
        />
      )}
    </div>
  );
}
// ==================== Daily Slots (Journée type) ====================
function DailySlotsPanel() {
  const [cfg, setCfg] = useState(null);
  const [categories, setCategories] = useState([]);
  useEffect(() => {
    api.get("/admin/daily-slots").then((r) => setCfg(r.data));
    api.get("/admin/vocab-categories").then((r) => setCategories(r.data));
  }, []);
  if (!cfg) return <div className="text-muted-foreground">{t("lessoneditor.chargement")}</div>;
  const addSlot = () => {
    setCfg({
      ...cfg,
      exercise_slots: [
        ...(cfg.exercise_slots || []),
        {
          id: `slot-${Date.now()}`,
          type: "vocab",
          minutes: 2,
          enabled: true,
          weight: 1.0,
          category: "*",
          tense: "*",
          difficulty_min: 1,
          difficulty_max: 5,
          label_fr: "Nouveau slot",
          label_en: "New slot",
        },
      ],
    });
  };
  const updateSlot = (i, patch) => {
    const arr = [...cfg.exercise_slots];
    arr[i] = { ...arr[i], ...patch };
    setCfg({ ...cfg, exercise_slots: arr });
  };
  const removeSlot = (i) =>
    setCfg({
      ...cfg,
      exercise_slots: cfg.exercise_slots.filter((_, j) => j !== i),
    });
  const moveUp = (i) => {
    if (i === 0) return;
    const arr = [...cfg.exercise_slots];
    [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
    setCfg({ ...cfg, exercise_slots: arr });
  };
  const save = async () => {
    try {
      await api.put("/admin/daily-slots", cfg);
      toast.success("Journée type enregistrée.");
    } catch {
      toast.error("Erreur");
    }
  };
  const TENSES = [
    "*",
    "past",
    "present",
    "imperative",
    "future",
    "jussive",
    "subjunctive",
  ];
  const TYPES = [
    "vocab",
    "conjugation",
    "review",
    "errors",
    "reading",
    "custom",
  ];
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border bg-card p-5">
        <h3 className="font-head font-bold mb-3">
          {t("admin.configuration_de_la_journee_type")}
        </h3>
        <p className="text-xs text-muted-foreground mb-4">
          {t("admin.structure_d_une_journee_d_entrainement")}{" "}
          <b>{t("admin.1_lecon_max_n_slots_d")}</b>. Le nombre de questions par
          slot s'adapte au temps quotidien de l'apprenant (répartition
          proportionnelle aux <b>poids</b>).
        </p>
        <div className="grid sm:grid-cols-3 gap-3 mb-4">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={cfg.lesson_enabled}
              onChange={(e) =>
                setCfg({ ...cfg, lesson_enabled: e.target.checked })
              }
            />
            <span className="text-sm">{t("admin.lecon_quotidienne_activee")}</span>
          </label>
          <div>
            <label className="text-xs text-muted-foreground">
              {t("admin.minutes_cible_lecon")}
            </label>
            <input
              type="number"
              min={1}
              max={30}
              value={cfg.lesson_minutes}
              onChange={(e) =>
                setCfg({
                  ...cfg,
                  lesson_minutes: parseInt(e.target.value, 10) || 6,
                })
              }
              className="w-full px-3 py-2 rounded-xl border bg-background"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">
              {t("admin.minutes_par_question")}
            </label>
            <input
              type="number"
              min={0.2}
              max={2}
              step={0.1}
              value={cfg.minutes_per_question}
              onChange={(e) =>
                setCfg({
                  ...cfg,
                  minutes_per_question: parseFloat(e.target.value) || 0.5,
                })
              }
              className="w-full px-3 py-2 rounded-xl border bg-background"
            />
          </div>
        </div>
        <button
          onClick={save}
          data-testid="slots-save"
          className="px-4 py-2 rounded-xl bg-primary text-primary-foreground font-semibold flex items-center gap-2"
        >
          <Save size={16} /> {t("lessoneditor.enregistrer")}
        </button>
      </div>
      <div className="rounded-2xl border bg-card p-5">
        <div className="flex items-center gap-2 mb-3">
          <h3 className="font-head font-bold">
            {t("admin.slots_d_exercices")}{cfg.exercise_slots?.length || 0})
          </h3>
          <button
            onClick={addSlot}
            className="ml-auto px-3 py-1.5 rounded-lg border text-sm hover:bg-secondary flex items-center gap-1"
          >
            <Plus size={14} /> {t("admin.ajouter_un_slot")}
          </button>
        </div>
        <div className="space-y-2">
          {(cfg.exercise_slots || []).map((s, i) => (
            <div
              key={s.id}
              className={`border rounded-xl p-3 ${s.enabled ? "" : "opacity-50"} bg-background`}
            >
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <button
                  onClick={() => moveUp(i)}
                  disabled={i === 0}
                  className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-30"
                >
                  ▲
                </button>
                <label className="flex items-center gap-1 text-xs">
                  <input
                    type="checkbox"
                    checked={s.enabled}
                    onChange={(e) =>
                      updateSlot(i, { enabled: e.target.checked })
                    }
                  />{" "}
                  {t("admin.actif")}
                </label>
                <select
                  value={s.type}
                  onChange={(e) => updateSlot(i, { type: e.target.value })}
                  className="px-2 py-1 rounded border bg-background text-sm"
                >
                  {TYPES.map((t) => (
                    <option key={t}>{t}</option>
                  ))}
                </select>
                <input
                  value={s.label_fr}
                  onChange={(e) => updateSlot(i, { label_fr: e.target.value })}
                  placeholder={t("admin.libelle_fr")}
                  className="flex-1 min-w-[120px] px-2 py-1 rounded border bg-background text-sm"
                />
                <span className="text-xs text-muted-foreground">{t("admin.min")}</span>
                <input
                  type="number"
                  min={1}
                  max={30}
                  value={s.minutes}
                  onChange={(e) =>
                    updateSlot(i, {
                      minutes: parseInt(e.target.value, 10) || 2,
                    })
                  }
                  className="w-16 px-2 py-1 rounded border bg-background text-sm"
                />
                <span className="text-xs text-muted-foreground">{t("admin.poids")}</span>
                <input
                  type="number"
                  min={0.1}
                  max={5}
                  step={0.1}
                  value={s.weight}
                  onChange={(e) =>
                    updateSlot(i, { weight: parseFloat(e.target.value) || 1.0 })
                  }
                  className="w-16 px-2 py-1 rounded border bg-background text-sm"
                />
                <button
                  onClick={() => removeSlot(i)}
                  className="text-destructive p-1"
                >
                  <Trash2 size={14} />
                </button>
              </div>
              {s.type === "vocab" && (
                <div className="flex items-center gap-2 text-xs pl-4">
                  <span className="text-muted-foreground">{t("admin.categorie")}</span>
                  <select
                    value={s.category}
                    onChange={(e) =>
                      updateSlot(i, { category: e.target.value })
                    }
                    className="px-2 py-1 rounded border bg-background"
                  >
                    <option value="*">{t("admin.auto_progression_90")}</option>
                    {categories.map((c) => (
                      <option key={c.category} value={c.category}>
                        {c.category} ({c.count})
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {s.type === "conjugation" && (
                <div className="flex items-center gap-2 text-xs pl-4">
                  <span className="text-muted-foreground">{t("admin.temps")}</span>
                  <select
                    value={s.tense}
                    onChange={(e) => updateSlot(i, { tense: e.target.value })}
                    className="px-2 py-1 rounded border bg-background"
                  >
                    {TENSES.map((tt) => (
                      <option key={tt}>{tt === "*" ? t("admin.tous") : tt}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex items-center gap-2 text-xs pl-4 mt-1">
                <span className="text-muted-foreground">{t("admin.difficulte")}</span>
                <input
                  type="number"
                  min={1}
                  max={5}
                  value={s.difficulty_min}
                  onChange={(e) =>
                    updateSlot(i, {
                      difficulty_min: parseInt(e.target.value, 10) || 1,
                    })
                  }
                  className="w-14 px-1 py-0.5 rounded border bg-background"
                />
                <span>→</span>
                <input
                  type="number"
                  min={1}
                  max={5}
                  value={s.difficulty_max}
                  onChange={(e) =>
                    updateSlot(i, {
                      difficulty_max: parseInt(e.target.value, 10) || 5,
                    })
                  }
                  className="w-14 px-1 py-0.5 rounded border bg-background"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
function UserLearningControls({ user, reloadUsers }) {
  const [level, setLevel] = useState(user.level || "A1");
  const [saving, setSaving] = useState(false);
  const update = async (alphabetMode = "keep") => {
    if (
      alphabetMode === "restart" &&
      !window.confirm(
        `Réinitialiser uniquement la progression alphabet de ${user.email} ?`,
      )
    ) {
      return;
    }
    setSaving(true);
    try {
      await api.put(`/admin/users/${user.id}/learning`, {
        level,
        alphabet_mode: alphabetMode,
      });
      toast.success(
        alphabetMode === "restart"
          ? "Alphabet réinitialisé : le Daily Plan reprend par l'alphabet."
          : "Parcours utilisateur mis à jour.",
      );
      reloadUsers();
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Impossible de modifier ce parcours.",
      );
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <select
        value={level}
        onChange={(e) => setLevel(e.target.value)}
        disabled={saving}
        aria-label={`Niveau de ${user.email}`}
        className="min-w-[5.5rem] rounded-lg border bg-background px-2 py-1 text-xs"
      >
        {["base", "A1", "A2", "B1", "B2", "C1", "C2"].map((value) => (
          <option key={value} value={value}>
            {value === "base" ? t("admin.base") : value}
          </option>
        ))}
      </select>
      <button
        type="button"
        onClick={() => update("keep")}
        disabled={saving}
        className="rounded-lg border px-2 py-1 text-xs font-semibold hover:bg-secondary disabled:opacity-50"
      >
        {t("admin.niveau")}
      </button>
      <button
        type="button"
        onClick={() => update("priority")}
        disabled={saving}
        className="rounded-lg bg-amber-500/15 px-2 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-500/25 disabled:opacity-50 dark:text-amber-300"
      >
        {t("admin.passer_alphabet")}
      </button>
      <button
        type="button"
        onClick={() => update("normal")}
        disabled={saving}
        className="rounded-lg bg-primary/10 px-2 py-1 text-xs font-semibold text-primary hover:bg-primary/20 disabled:opacity-50"
      >
        {t("admin.parcours_normal")}
      </button>
      <button
        type="button"
        onClick={() => update("restart")}
        disabled={saving}
        className="rounded-lg border border-destructive/30 px-2 py-1 text-xs font-semibold text-destructive hover:bg-destructive/10 disabled:opacity-50"
      >
        {t("admin.refaire_alphabet")}
      </button>
    </div>
  );
}
// ==================== Main Admin ====================
export default function Admin() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("stats");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const reloadUsers = () =>
    api
      .get("/admin/users")
      .then((r) => setUsers(r.data))
      .catch(() => {});
  useEffect(() => {
    if (user && user.role !== "admin") {
      nav("/dashboard");
      return;
    }
    api
      .get("/admin/stats")
      .then((r) => setStats(r.data))
      .catch(() => {});
    api
      .get("/admin/users")
      .then((r) => setUsers(r.data))
      .catch(() => {});
  }, [user, nav]);
  if (!stats) return <div className="text-muted-foreground">{t("lessoneditor.chargement")}</div>;
  const TABS = [
    ["stats", t("admin.tabs.stats"), BookOpen],
    ["users", t("admin.tabs.users"), Users],
    ["billing", "Abonnements", CreditCard],
    ["affiliates", "Affiliés", Link2],
    ["support", "Support", MessageCircle],
    ["rules", t("admin.tabs.rules"), Settings2],
    ["vocab", t("admin.tabs.vocab"), Book],
    ["verbs", t("admin.tabs.verbs"), Type],
    ["questions", t("admin.tabs.questions"), Brain],
    ["lessons", "Leçons", BookOpen],
    ["slots", "Journée type", ListChecks],
    ["plan", t("admin.tabs.plan"), Layers],
  ];
  return (
    <div>
      <h1 className="font-head text-3xl font-bold mb-1">{t("admin.title")}</h1>
      <p className="text-muted-foreground mb-6">
        {t("admin.vue_d_ensemble_donnees_dynamiques_et")}
      </p>
      <div className="flex gap-2 mb-6 flex-wrap">
        {TABS.map(([k, l, I]) => (
          <button
            key={k}
            data-testid={`admin-tab-${k}`}
            onClick={() => setTab(k)}
            className={`px-4 py-2 rounded-xl text-sm font-medium border transition flex items-center gap-2 ${tab === k ? "bg-primary text-primary-foreground border-primary" : "hover:bg-secondary"}`}
          >
            <I size={14} /> {l}
          </button>
        ))}
      </div>
      {tab === "stats" && (
        <div className="space-y-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat icon={Users} label={t("admin.utilisateurs")} value={stats.users} />
            <Stat icon={BookOpen} label={t("admin.lecons")} value={stats.lessons} />
            <Stat
              icon={FileText}
              label={t("admin.textes_de_lecture")}
              value={stats.texts}
            />
            <Stat
              icon={GraduationCap}
              label={t("admin.examens_reussis_passes")}
              value={`${stats.exams_passed}/${stats.exams_taken}`}
            />
          </div>
          <div className="rounded-2xl border bg-card p-5">
            <h2 className="font-head font-bold mb-3">{t("admin.derniers_inscrits")}</h2>
            {stats.recent_users.map((u, i) => (
              <div
                key={i}
                className="flex items-center justify-between py-2 border-b last:border-0 text-sm"
              >
                <div>
                  <span className="font-medium">{u.name}</span>{" "}
                  <span className="text-muted-foreground">{u.email}</span>
                </div>
                <div className="text-muted-foreground">
                  {u.role} · {u.level} · 🔥{u.streak}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {tab === "users" && (
        <div className="rounded-2xl border bg-card overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-2 text-xs font-semibold bg-secondary">
            <div className="col-span-3">{t("admin.utilisateur")}</div>
            <div>{t("admin.niveau")}</div>
            <div className="col-span-2">{t("admin.lecture")}</div>
            <div>{t("publicchrome.alphabet")}</div>
            <div>{t("admin.acces")}</div>
            <div>{t("admin.plan")}</div>
            <div>{t("admin.maitrisees")}</div>
            <div className="col-span-2">{t("admin.actions_parcours")}</div>
          </div>
          {users.map((u) => (
            <div
              key={u.id}
              className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-3 px-4 py-3 border-t text-sm items-center"
            >
              <div className="md:col-span-3 min-w-0">
                <div className="font-medium truncate">
                  {u.name}{" "}
                  {u.role === "admin" && (
                    <span className="text-xs text-primary">{t("admin.admin")}</span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {u.email}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="md:hidden text-xs text-muted-foreground">
                  {t("admin.niveau_2")}
                </span>
                <span className="font-semibold">{u.level || "A1"}</span>
              </div>
              <div className="md:col-span-2 text-xs">
                <span className="md:hidden text-muted-foreground">
                  {t("admin.lecture_2")}{" "}
                </span>
                {u.reading_level || "non évaluée"}
                {u.reading_beginner && (
                  <span className="ml-1 text-amber-600">
                    {t("admin.alphabet_prioritaire")}
                  </span>
                )}
              </div>
              <div className="text-xs">
                <span className="md:hidden text-muted-foreground">
                  {t("admin.alphabet")}{" "}
                </span>
                <span className="font-semibold">
                  {u.alphabet_mastery_pct ?? 0}%
                </span>
              </div>
              <div className="text-xs">
                <span className="md:hidden text-muted-foreground">
                  {t("admin.acces_2")}{" "}
                </span>
                {u.account_type}
              </div>
              <div className="text-xs">
                <span className="md:hidden text-muted-foreground">{t("admin.plan_2")} </span>
                {u.plan}
              </div>
              <div>
                <span className="md:hidden text-xs text-muted-foreground">
                  {t("admin.maitrisees_2")}{" "}
                </span>
                {u.mastered}
              </div>
              <div className="md:col-span-2">
                <UserLearningControls user={u} reloadUsers={reloadUsers} />
              </div>
            </div>
          ))}
        </div>
      )}
      {tab === "billing" && (
        <BillingAdminPanel users={users} reloadUsers={reloadUsers} />
      )}
      {tab === "affiliates" && <AffiliateAdminPanel users={users} />}
      {tab === "support" && <AdminSupportMessages />}
      {tab === "rules" && <RulesPanel />}
      {tab === "vocab" && <VocabPanel />}
      {tab === "verbs" && <VerbsPanel />}
      {tab === "questions" && <QuestionsPanel />}
      {tab === "lessons" && <LessonsPanel />}
      {tab === "slots" && <DailySlotsPanel />}
      {tab === "plan" && <DailyPlanBuilder />}
    </div>
  );
}
